import { useEffect, useMemo, useState } from "react";
import { useTheme } from "../contexts/ThemeContext";
import { useAuth } from "../contexts/AuthContext";
import { getDashboard } from "../services/dashboardService";
import { listFinanceiro } from "../services/financeiroService";
import { tipoTarefaIcon } from "../services/tarefasService";
import { saldoRestante, diasAtraso } from "../utils/financeiro";
import { formatMoeda } from "../utils/moeda";
import StatCard from "../components/ui/StatCard";
import Loading, { ErrorBox } from "../components/ui/Loading";

function formatDateBr(iso) {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}`;
}

export default function DashboardPage({ onNavigate }) {
  const { C } = useTheme();
  const { profile } = useAuth();
  // PR3 moeda-por-guia: símbolo vem da moeda do guia (post-PR2, todo lançamento
  // do guia já é dessa moeda). Fallback defensivo pra "EUR" enquanto profile
  // carrega na primeira render.
  const moeda = profile?.moeda ?? "EUR";
  // Wrapper local: mantém zero decimais (idem ao formatCurrency antigo) pros
  // cards do Dashboard. "€ 1.234" pra EUR-only — byte-idêntico ao output anterior.
  const fmt = (v) => formatMoeda(v, moeda, { decimals: 0 });
  const [data, setData] = useState(null);
  // Lista completa de lançamentos — buscada em paralelo pra calcular atrasos.
  // Se o fetch falhar, cai silenciosamente pra [] e o card de alerta some —
  // dashboard principal continua funcionando.
  const [lista, setLista] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    Promise.all([getDashboard(), listFinanceiro().catch(() => [])])
      .then(([dashboard, lancamentos]) => {
        if (active) {
          setData(dashboard);
          setLista(lancamentos ?? []);
          setError(null);
        }
      })
      .catch(() => {
        if (active) setError("Não foi possível carregar o dashboard.");
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, []);

  // Atrasos: conta lançamentos com saldo restante > 0 e vencimento já passado,
  // agrupados por tipo. PR3 moeda-por-guia: bucket único — todo Financeiro do
  // guia já é na moeda dele (write-side PR2). Símbolo via profile.moeda.
  const atrasos = useMemo(() => {
    const r = {
      receber: { count: 0, total: 0 },
      pagar: { count: 0, total: 0 },
    };
    for (const f of lista) {
      if (f.status === 2) continue; // Pago/Recebido
      const saldo = saldoRestante(f);
      if (saldo <= 0) continue;
      const dias = f.vencimento ? diasAtraso(f.vencimento) : null;
      if (dias === null || dias <= 0) continue; // não atrasado
      const bucket = f.tipo === 0 ? r.receber : r.pagar;
      bucket.count++;
      bucket.total += saldo;
    }
    return { ...r, total: r.receber.count + r.pagar.count };
  }, [lista]);

  return (
    <div>
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: C.text, margin: 0 }}>
          Início
        </h1>
        <p style={{ color: C.textMuted, fontSize: 13, margin: "4px 0 0" }}>
          Visão geral do seu negócio · turismo.tecmovel.com.br
        </p>
      </div>

      {loading && <Loading />}
      {error && <ErrorBox message={error} />}

      {data && !loading && (
        <>
          {/* ─── Alerta de contas atrasadas — renderiza só se houver ─── */}
          {atrasos.total > 0 && (
            <div
              style={{
                background: C.redDim,
                border: `1px solid ${C.red}`,
                borderRadius: 12,
                padding: "16px 20px",
                marginBottom: 14,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 16,
                flexWrap: "wrap",
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 700,
                    color: C.red,
                    marginBottom: 4,
                  }}
                >
                  ⚠️ Você tem {atrasos.total} conta
                  {atrasos.total === 1 ? "" : "s"} atrasada
                  {atrasos.total === 1 ? "" : "s"}
                </div>
                <div style={{ fontSize: 12, color: C.textMuted }}>
                  {atrasos.receber.count > 0 && atrasos.pagar.count > 0
                    ? `${atrasos.receber.count} a receber (${fmt(
                        atrasos.receber.total
                      )}) · ${atrasos.pagar.count} a pagar (${fmt(
                        atrasos.pagar.total
                      )})`
                    : atrasos.receber.count > 0
                    ? `${atrasos.receber.count} a receber (${fmt(
                        atrasos.receber.total
                      )})`
                    : `${atrasos.pagar.count} a pagar (${fmt(
                        atrasos.pagar.total
                      )})`}
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {atrasos.receber.count > 0 && (
                  <button
                    onClick={() => onNavigate?.("contas-receber")}
                    style={{
                      padding: "8px 16px",
                      borderRadius: 8,
                      border: `1px solid ${C.red}`,
                      background: "#fff",
                      color: C.red,
                      fontSize: 12,
                      fontWeight: 700,
                      cursor: "pointer",
                      fontFamily: "'DM Sans', sans-serif",
                    }}
                  >
                    Ver a receber
                  </button>
                )}
                {atrasos.pagar.count > 0 && (
                  <button
                    onClick={() => onNavigate?.("contas-pagar")}
                    style={{
                      padding: "8px 16px",
                      borderRadius: 8,
                      border: `1px solid ${C.red}`,
                      background: "#fff",
                      color: C.red,
                      fontSize: 12,
                      fontWeight: 700,
                      cursor: "pointer",
                      fontFamily: "'DM Sans', sans-serif",
                    }}
                  >
                    Ver a pagar
                  </button>
                )}
              </div>
            </div>
          )}

          <div style={{ display: "flex", gap: 14, marginBottom: 14, flexWrap: "wrap" }}>
            <StatCard
              icon="📅"
              label="Próximos Grupos"
              value={data.proximosGrupos?.length ?? 0}
              sub="Confirmados / em montagem"
              color={C.blue}
            />
            <StatCard
              icon="📆"
              label="Dias Livres"
              value={data.diasLivres}
              sub="No mês corrente"
              color={C.accent}
            />
            <StatCard
              icon="👥"
              label="Clientes Ativos"
              value={data.clientesAtivos}
              sub="Cadastrados"
              color={C.purple}
            />
          </div>

          <div style={{ display: "flex", gap: 14, marginBottom: 24, flexWrap: "wrap" }}>
            {/* Trio de planejamento do mês — base "vence no mês" (C2.3). */}
            <StatCard
              icon="📥"
              label="A receber no mês"
              value={fmt(data.aReceberMes)}
              sub="Vence no mês corrente"
              color={C.green}
            />
            <StatCard
              icon="📤"
              label="A pagar no mês"
              value={fmt(data.aPagarMes)}
              sub="Vence no mês corrente"
              color={C.red}
            />
            <StatCard
              icon={data.saldoProjetadoMes >= 0 ? "📈" : "⚠️"}
              label="Saldo projetado do mês"
              value={fmt(data.saldoProjetadoMes)}
              sub="A receber − a pagar"
              color={data.saldoProjetadoMes >= 0 ? C.green : C.red}
            />
            {/* Realizado — independente do trio: o que entrou de fato no caixa. */}
            <StatCard
              icon="💰"
              label="Recebido de fato"
              value={fmt(data.recebidoDeFatoMes)}
              sub="Parcelas pagas no mês"
              color={C.blue}
            />
          </div>

          <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
            {/* ─── Card Tarefas pendentes (substitui Faturamento Mensal) ─── */}
            <div
              onClick={() => onNavigate?.("roteiros")}
              style={{
                flex: 1,
                minWidth: 280,
                background: C.bgCard,
                borderRadius: 14,
                padding: 22,
                border: `1px solid ${
                  data.tarefasVencidasCount > 0 ? C.red : C.border
                }`,
                cursor: "pointer",
                transition: "all 0.15s",
              }}
              title="Ver tarefas nos roteiros"
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  marginBottom: 14,
                }}
              >
                <div>
                  <h3
                    style={{
                      margin: 0,
                      fontSize: 14,
                      fontWeight: 600,
                      color: C.text,
                    }}
                  >
                    📋 Tarefas pendentes
                  </h3>
                  <div style={{ marginTop: 6, display: "flex", alignItems: "baseline", gap: 10 }}>
                    <span
                      style={{
                        fontSize: 28,
                        fontWeight: 700,
                        color: C.text,
                        lineHeight: 1,
                      }}
                    >
                      {data.tarefasPendentesCount ?? 0}
                    </span>
                    {data.tarefasVencidasCount > 0 && (
                      <span
                        style={{
                          fontSize: 12,
                          fontWeight: 700,
                          color: C.red,
                        }}
                      >
                        {data.tarefasVencidasCount} vencida
                        {data.tarefasVencidasCount === 1 ? "" : "s"}
                      </span>
                    )}
                  </div>
                </div>
                <span style={{ color: C.textDim, fontSize: 16 }}>›</span>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {(data.proximas3 ?? []).map((t) => {
                  const vencida =
                    t.dataVencimento &&
                    new Date(t.dataVencimento) <
                      new Date(new Date().setHours(0, 0, 0, 0));
                  return (
                    <div
                      key={t.tarefaId}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "8px 12px",
                        background: C.bg,
                        borderRadius: 8,
                        borderLeft: `3px solid ${vencida ? C.red : C.accent}`,
                      }}
                    >
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div
                          style={{
                            fontSize: 12,
                            fontWeight: 600,
                            color: C.text,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {tipoTarefaIcon(t.tipo)} {t.tituloTarefa}
                        </div>
                        <div
                          style={{
                            fontSize: 10,
                            color: C.textMuted,
                            marginTop: 2,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {t.tituloRoteiro}
                        </div>
                      </div>
                      <div
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          color: vencida ? C.red : C.accent,
                          marginLeft: 10,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {t.dataVencimento ? formatDateBr(t.dataVencimento) : "sem prazo"}
                      </div>
                    </div>
                  );
                })}
                {(!data.proximas3 || data.proximas3.length === 0) && (
                  <div style={{ fontSize: 11, color: C.textMuted, padding: "6px 0" }}>
                    Nenhuma tarefa pendente. Bom trabalho! 🎉
                  </div>
                )}
              </div>
            </div>

            {/* ─── Próximos grupos (agora flex 2 pra preencher o espaço) ─── */}
            <div
              style={{
                flex: 2,
                minWidth: 300,
                background: C.bgCard,
                borderRadius: 14,
                padding: 22,
                border: `1px solid ${C.border}`,
              }}
            >
              <h3
                style={{
                  margin: "0 0 16px",
                  fontSize: 14,
                  fontWeight: 600,
                  color: C.text,
                }}
              >
                Próximos Grupos
              </h3>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {(data.proximosGrupos ?? []).slice(0, 5).map((r) => (
                  <div
                    key={r.roteiroId}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "10px 12px",
                      background: C.bg,
                      borderRadius: 10,
                    }}
                  >
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>
                        {r.clienteNome ?? "—"}
                      </div>
                      <div style={{ fontSize: 11, color: C.textMuted, marginTop: 2 }}>
                        {r.titulo}
                      </div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontSize: 12, color: C.accent, fontWeight: 600 }}>
                        {formatDateBr(r.dataInicio)}
                      </div>
                      <div style={{ fontSize: 10, color: C.textMuted }}>{r.dias} dias</div>
                    </div>
                  </div>
                ))}
                {(!data.proximosGrupos || data.proximosGrupos.length === 0) && (
                  <div style={{ fontSize: 12, color: C.textMuted }}>
                    Nenhum grupo próximo.
                  </div>
                )}
              </div>
            </div>
          </div>

          <div
            style={{
              marginTop: 16,
              background: C.bgCard,
              borderRadius: 14,
              padding: 22,
              border: `1px solid ${C.border}`,
            }}
          >
            <h3
              style={{
                margin: "0 0 14px",
                fontSize: 14,
                fontWeight: 600,
                color: C.text,
              }}
            >
              ⚠️ Alertas
            </h3>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {(data.alertas ?? []).map((a, i) => (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "10px 14px",
                    background: C[a.tipo + "Dim"] || C.accentDim,
                    borderRadius: 8,
                    borderLeft: `3px solid ${C[a.tipo] || C.accent}`,
                  }}
                >
                  <span
                    style={{
                      fontSize: 12,
                      color: C[a.tipo] || C.accent,
                      fontWeight: 500,
                    }}
                  >
                    {a.mensagem}
                  </span>
                </div>
              ))}
              {(!data.alertas || data.alertas.length === 0) && (
                <div style={{ fontSize: 12, color: C.textMuted }}>Nenhum alerta.</div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
